(() => {
  'use strict';

  if (customElements.get('maj-circle-carousel')) return;

  class MajCircleCarousel extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.track = this.querySelector('[data-maj-circle-track]');
      this.previousButton = this.querySelector('[data-maj-circle-prev]');
      this.nextButton = this.querySelector('[data-maj-circle-next]');
      this.pagination = this.querySelector('[data-maj-circle-pagination]');

      if (!this.track || !this.previousButton || !this.nextButton || !this.pagination) return;

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.previousButton.addEventListener('click', () => this.goToPage(this.currentPage - 1), { signal });
      this.nextButton.addEventListener('click', () => this.goToPage(this.currentPage + 1), { signal });
      this.track.addEventListener('scroll', () => this.scheduleUpdate(), { passive: true, signal });
      this.track.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.goToPage(this.currentPage - 1);
          }
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.goToPage(this.currentPage + 1);
          }
        },
        { signal }
      );

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.rebuild());
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', () => this.rebuild(), { signal });
      }
      this.rebuild();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.dotAbortController?.abort();
      this.resizeObserver?.disconnect();
      if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
    }

    get maximumScroll() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    rebuild() {
      if (!this.track.clientWidth) return;

      const maximumScroll = this.maximumScroll;
      const pageCount = maximumScroll > 2 ? Math.ceil(this.track.scrollWidth / this.track.clientWidth) : 1;
      this.pagePositions = Array.from({ length: pageCount }, (_, index) =>
        pageCount === 1 ? 0 : (maximumScroll * index) / (pageCount - 1)
      );

      this.dotAbortController?.abort();
      this.dotAbortController = new AbortController();
      while (this.pagination.firstChild) this.pagination.firstChild.remove();
      const paginationClass = [...this.pagination.classList].find((name) => name.startsWith('maj-sq-pagination-'));
      const dotClass = paginationClass?.replace('pagination', 'dot') || '';

      this.pagePositions.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        if (dotClass) dot.className = dotClass;
        dot.setAttribute('aria-label', `Go to category page ${index + 1}`);
        dot.addEventListener('click', () => this.goToPage(index), {
          signal: this.dotAbortController.signal,
        });
        this.pagination.append(dot);
      });

      this.pagination.hidden = pageCount <= 1;
      this.update();
    }

    scheduleUpdate() {
      if (this.animationFrame) return;
      this.animationFrame = requestAnimationFrame(() => {
        this.animationFrame = null;
        this.update();
      });
    }

    update() {
      if (!this.pagePositions?.length) return;

      const left = Math.max(0, this.track.scrollLeft);
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      this.pagePositions.forEach((position, index) => {
        const distance = Math.abs(position - left);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      this.currentPage = nearestIndex;
      this.previousButton.disabled = left <= 2;
      this.nextButton.disabled = left >= this.maximumScroll - 2;

      this.pagination.querySelectorAll('button').forEach((dot, index) => {
        if (index === nearestIndex) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    goToPage(index) {
      if (!this.pagePositions?.length) return;

      const targetIndex = Math.max(0, Math.min(index, this.pagePositions.length - 1));
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.currentPage = targetIndex;
      this.track.scrollTo({
        left: this.pagePositions[targetIndex] || 0,
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
    }
  }

  customElements.define('maj-circle-carousel', MajCircleCarousel);
})();